import { useState, useRef, useCallback, useEffect } from "react";
import { useScribe, CommitStrategy } from "@elevenlabs/react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Loader2, Sparkles, Languages } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export interface BridgetteParsedJob {
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  address: string | null;
  property_class: "residential" | "commercial" | null;
  project_type: "renovation" | "new_construction" | "build_out" | null;
  subtrades: string[];
  notes: string | null;
}

interface Props {
  predefinedSubtrades?: string[];
  onResult: (parsed: BridgetteParsedJob, transcript: string) => void;
  disabled?: boolean;
}

type Lang = "en" | "ru";

const LANG_LABEL: Record<Lang, string> = {
  en: "English",
  ru: "Русский",
};

/**
 * Bridgette voice-dictation button for the GC Add-Job form.
 * Press → talk → release → auto-fill all fields. Supports English & Russian.
 */
export function BridgetteVoiceCapture({ predefinedSubtrades, onResult, disabled }: Props) {
  const [isRecording, setIsRecording] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [livePartial, setLivePartial] = useState("");
  const [lang, setLang] = useState<Lang>("en");
  const committedRef = useRef<string[]>([]);

  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    commitStrategy: CommitStrategy.VAD,
    onPartialTranscript: (data: any) => setLivePartial(data?.text ?? ""),
    onCommittedTranscript: (data: any) => {
      if (data?.text) committedRef.current.push(data.text);
      setLivePartial("");
    },
  });

  const stopAndParse = useCallback(async () => {
    setIsRecording(false);
    try {
      await scribe.disconnect();
    } catch {}

    const transcript = [...committedRef.current, livePartial].filter(Boolean).join(" ").trim();
    committedRef.current = [];
    setLivePartial("");

    if (!transcript) {
      toast.error("Didn't catch that — try again");
      return;
    }

    setIsParsing(true);
    try {
      const { data, error } = await supabase.functions.invoke("bridgette-parse-job", {
        body: { transcript, predefinedSubtrades, sourceLanguage: lang },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "Failed to parse");
      onResult(data.data as BridgetteParsedJob, transcript);
      toast.success(lang === "ru" ? "Bridgette заполнила форму ✨" : "Bridgette filled the form ✨");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message ?? "Bridgette couldn't parse that");
    } finally {
      setIsParsing(false);
    }
  }, [scribe, livePartial, predefinedSubtrades, onResult, lang]);

  const startRecording = useCallback(async () => {
    if (disabled || isRecording || isParsing) return;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data, error } = await supabase.functions.invoke("elevenlabs-scribe-token", {
        body: {},
      });
      console.log("[Bridgette] token response:", { data, error });
      if (error) throw new Error(error.message ?? "Token request failed");
      if (!data?.token) throw new Error("No token received");

      committedRef.current = [];
      setLivePartial("");
      await scribe.connect({
        token: data.token,
        microphone: { echoCancellation: true, noiseSuppression: true },
      });
      setIsRecording(true);
    } catch (e: any) {
      console.error("[Bridgette] start error:", e);
      toast.error(e?.message ?? "Mic permission needed");
    }
  }, [disabled, isRecording, isParsing, scribe]);

  useEffect(() => {
    return () => {
      try {
        scribe.disconnect();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div>
            <div className="font-semibold text-sm">
              {lang === "ru" ? "Расскажи Bridgette" : "Talk it out with Bridgette"}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === "ru"
                ? "Нажми микрофон, опиши работу, нажми стоп — поля заполнятся."
                : "Tap mic, describe the job, tap stop — fields auto-fill."}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <div className="inline-flex rounded-md border border-border bg-background p-0.5">
            {(["en", "ru"] as Lang[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => !isRecording && setLang(l)}
                disabled={isRecording}
                className={cn(
                  "px-2.5 py-1 text-xs font-medium rounded-sm transition-colors",
                  lang === l
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Languages className="inline h-3 w-3 mr-1" />
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>

          {!isRecording ? (
            <Button
              type="button"
              onClick={startRecording}
              disabled={disabled || isParsing}
              className="gap-2"
            >
              {isParsing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {lang === "ru" ? "Заполняю…" : "Filling form…"}
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4" />
                  {lang === "ru" ? "Начать" : "Start dictation"}
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={stopAndParse}
              variant="destructive"
              className={cn("gap-2 animate-pulse")}
            >
              <MicOff className="h-4 w-4" />
              {lang === "ru" ? "Стоп" : "Stop & fill"}
            </Button>
          )}
        </div>
      </div>

      {(isRecording || livePartial || committedRef.current.length > 0) && (
        <div className="text-xs rounded-md bg-background/60 border border-border p-2 max-h-24 overflow-y-auto">
          <span className="text-muted-foreground">{committedRef.current.join(" ")} </span>
          <span className="italic text-foreground">{livePartial}</span>
          {isRecording && !livePartial && committedRef.current.length === 0 && (
            <span className="text-muted-foreground">
              {lang === "ru" ? "Слушаю…" : "Listening…"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
